import React from 'react';
import { v4 as uuid } from 'uuid';
import {
  FileText,
  Mail,
  Phone,
  MessageSquare,
  ChevronDown,
  ToggleLeft,
  CheckSquare,
  EyeOff,
  Send,
  Plus
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useEditorStore } from '../../store/editorStore';

// ── Templates ─────────────────────────────────────────────────

const FORM_TEMPLATES = [
  {
    id: 'contact',
    label: 'Contact Form',
    description: 'Name, email & message',
    icon: '💬',
    fields: [
      { id: uuid(), type: 'text',     name: 'name',    label: 'Your Name',      placeholder: 'John Doe',           required: true  },
      { id: uuid(), type: 'email',    name: 'email',   label: 'Email Address',  placeholder: 'you@example.com',    required: true  },
      { id: uuid(), type: 'textarea', name: 'message', label: 'Message',        placeholder: 'Write your message...', required: true },
      { id: uuid(), type: 'submit',   name: 'submit',  label: 'Send Message',   required: false },
    ],
  },
  {
    id: 'newsletter',
    label: 'Newsletter Signup',
    description: 'Email capture',
    icon: '📧',
    fields: [
      { id: uuid(), type: 'text',   name: 'name',  label: 'First Name',    placeholder: 'Your name',       required: true  },
      { id: uuid(), type: 'email',  name: 'email', label: 'Email Address', placeholder: 'Enter your email', required: true  },
      { id: uuid(), type: 'submit', name: 'submit', label: 'Subscribe →',  required: false },
    ],
  },
  {
    id: 'lead',
    label: 'Lead Generation',
    description: 'Full contact details',
    icon: '🎯',
    fields: [
      { id: uuid(), type: 'text',   name: 'name',     label: 'Full Name',         placeholder: 'Full Name',          required: true  },
      { id: uuid(), type: 'email',  name: 'email',    label: 'Email',             placeholder: 'Email address',       required: true  },
      { id: uuid(), type: 'phone',  name: 'phone',    label: 'Phone',             placeholder: '+1 (555) 000-0000',   required: false },
      { id: uuid(), type: 'select', name: 'interest', label: "I'm interested in", required: false,
        options: [
          { label: 'Product Info',  value: 'product'     },
          { label: 'Pricing',       value: 'pricing'     },
          { label: 'Partnership',   value: 'partnership' },
        ],
      },
      { id: uuid(), type: 'submit', name: 'submit', label: 'Get Started →', required: false },
    ],
  },
  {
    id: 'quiz',
    label: 'Quiz / Survey',
    description: 'Radio & checkbox fields',
    icon: '📊',
    fields: [
      { id: uuid(), type: 'text',     name: 'name',     label: 'Your Name',       placeholder: 'Your name', required: true },
      { id: uuid(), type: 'radio',    name: 'gender',   label: 'Gender',          required: false,
        options: [{ label: 'Male', value: 'male' }, { label: 'Female', value: 'female' }, { label: 'Other', value: 'other' }],
      },
      { id: uuid(), type: 'select',   name: 'age',      label: 'Age Group',       required: false,
        options: [
          { label: '18–25', value: '18-25' }, { label: '26–35', value: '26-35' },
          { label: '36–45', value: '36-45' }, { label: '46+',   value: '46-plus' },
        ],
      },
      { id: uuid(), type: 'checkbox', name: 'agree',    label: 'I agree to the terms & conditions', required: true },
      { id: uuid(), type: 'submit',   name: 'submit',   label: 'Submit',          required: false },
    ],
  },
  {
    id: 'booking',
    label: 'Appointment Booking',
    description: 'Name, phone & request',
    icon: '📅',
    fields: [
      { id: uuid(), type: 'text',     name: 'name',    label: 'Full Name',    placeholder: 'Your name',         required: true  },
      { id: uuid(), type: 'phone',    name: 'phone',   label: 'Phone Number', placeholder: '+1 (555) 000-0000', required: true  },
      { id: uuid(), type: 'email',    name: 'email',   label: 'Email',        placeholder: 'Email (optional)',  required: false },
      { id: uuid(), type: 'textarea', name: 'notes',   label: 'Special Requests', placeholder: 'Any notes for us?', required: false },
      { id: uuid(), type: 'submit',   name: 'submit',  label: 'Book Now',     required: false },
    ],
  },
];

// ── Field type reference ───────────────────────────────────────

const FIELD_TYPES = [
  { type: 'text',     icon: <FileText  size={13} />, label: 'Text',      desc: 'Single-line text input',         color: 'var(--brand-primary)' },
  { type: 'email',    icon: <Mail      size={13} />, label: 'Email',     desc: 'Email address with validation',  color: '#3b82f6' },
  { type: 'phone',    icon: <Phone     size={13} />, label: 'Phone',     desc: 'Phone / tel input',              color: '#10b981' },
  { type: 'textarea', icon: <MessageSquare size={13} />, label: 'Textarea', desc: 'Multi-line text area',       color: '#8b5cf6' },
  { type: 'select',   icon: <ChevronDown size={13} />, label: 'Dropdown', desc: 'Select from options',          color: '#f59e0b' },
  { type: 'checkbox', icon: <CheckSquare size={13} />, label: 'Checkbox', desc: 'Single toggle checkbox',       color: '#06b6d4' },
  { type: 'radio',    icon: <ToggleLeft size={13} />, label: 'Radio',    desc: 'Choose one from a group',       color: '#ec4899' },
  { type: 'hidden',   icon: <EyeOff    size={13} />, label: 'Hidden',   desc: 'Hidden field (tracking etc.)',   color: '#6b7280' },
  { type: 'submit',   icon: <Send      size={13} />, label: 'Submit',   desc: 'Submit / CTA button',            color: '#ef4444' },
];

// ── Default form style ────────────────────────────────────────

function defaultStyle() {
  return {
    backgroundColor: 'rgba(15, 15, 30, 0.92)',
    textColor: '#ffffff',
    accentColor: 'var(--brand-primary)',
    buttonColor: 'var(--brand-primary)',
    borderRadius: 12,
  };
}

// ── Component ─────────────────────────────────────────────────

export default function FormsPanel() {
  const activePageId      = useEditorStore((s) => s.activePageId);
  const selectedLayerIds  = useEditorStore((s) => s.selectedLayerIds);
  const addLayer          = useEditorStore((s) => s.addLayer);
  const updateLayerData   = useEditorStore((s) => s.updateLayerData);
  const selectLayer       = useEditorStore((s) => s.selectLayer);

  function ensurePage() {
    if (!activePageId) { toast.error('Select a page first'); return false; }
    return true;
  }

  /** Returns the layer ID of the "active" form to append fields to.
   *  Priority: selected form layer → first form layer on the page → null */
  function getActiveFormLayerId() {
    const state = useEditorStore.getState();
    if (!state.story || !activePageId) return null;
    const page = state.story.pages.find((p) => p.id === activePageId);
    if (!page) return null;

    // 1. Prefer whichever form layer is currently selected
    const selectedFormId = selectedLayerIds.find(
      (id) => page.layers.find((l) => l.id === id && l.data.type === 'form'),
    );
    if (selectedFormId) return selectedFormId;

    // 2. Fall back to the first form on the page
    const firstForm = page.layers.find((l) => l.data.type === 'form');
    return firstForm?.id ?? null;
  }

  /** Append a field to an existing form layer (always reads live store state). */
  function appendFieldToForm(formLayerId, field) {
    const state = useEditorStore.getState();
    if (!state.story) return;
    for (const page of state.story.pages) {
      const layer = page.layers.find((l) => l.id === formLayerId);
      if (layer) {
        const currentFields = layer.data.fields || [];
        updateLayerData(formLayerId, { fields: [...currentFields, field] });
        selectLayer(formLayerId);
        return;
      }
    }
  }

  function makeField(type) {
    return {
      id: uuid(),
      type,
      name: `${type}_${Date.now()}`,
      label: (FIELD_TYPES.find((f) => f.type === type)?.label ?? type) + ' Field',
      placeholder: !['submit', 'checkbox', 'radio', 'hidden'].includes(type)
        ? `Enter ${type}...`
        : undefined,
      required: type !== 'submit' && type !== 'hidden',
      options: (type === 'select' || type === 'radio')
        ? [{ label: 'Option 1', value: 'opt1' }, { label: 'Option 2', value: 'opt2' }]
        : undefined,
      defaultValue: type === 'hidden' ? '' : undefined,
    };
  }

  function addFormTemplate(tpl) {
    if (!ensurePage()) return;
    addLayer(activePageId, {
      type: 'form',
      formId: uuid(),
      formTitle: tpl.label,
      actionUrl: '',
      actionXhrUrl: '',
      fields: tpl.fields.map((f) => ({ ...f, id: uuid() })),
      successMessage: "Thank you! We'll be in touch.",
      errorMessage: 'Something went wrong. Please try again.',
      style: defaultStyle(),
    }, { x: 5, y: 15, width: 90, height: 72 });
    toast.success(`${tpl.label} added!`);
  }

  function addBlankForm() {
    if (!ensurePage()) return;
    addLayer(activePageId, {
      type: 'form',
      formId: uuid(),
      formTitle: '',
      actionUrl: '',
      actionXhrUrl: '',
      fields: [],
      successMessage: 'Thank you!',
      errorMessage: 'Something went wrong.',
      style: defaultStyle(),
    }, { x: 5, y: 15, width: 90, height: 72 });
    toast.success('Blank form added — select it, then add fields below');
  }

  /** Add a field to the existing form on the page, or create a new form if none exists. */
  function addFieldToForm(type) {
    if (!ensurePage()) return;
    const existingFormId = getActiveFormLayerId();
    const newField = makeField(type);

    if (existingFormId) {
      // Append to the existing form
      appendFieldToForm(existingFormId, newField);
      toast.success(`${FIELD_TYPES.find((f) => f.type === type)?.label} field added`);
    } else {
      // No form on this page yet — create one with this field + a submit button
      addLayer(activePageId, {
        type: 'form',
        formId: uuid(),
        formTitle: '',
        actionUrl: '',
        actionXhrUrl: '',
        fields: [
          newField,
          { id: uuid(), type: 'submit', name: 'submit', label: 'Submit', required: false },
        ],
        successMessage: 'Thank you!',
        errorMessage: 'Something went wrong.',
        style: defaultStyle(),
      }, { x: 5, y: 15, width: 90, height: 72 });
      toast.success(`Form created with ${type} field`);
    }
  }

  // Derive which form is active for the UI status banner
  const activeFormLayerId = getActiveFormLayerId();
  const activeFormLabel = (() => {
    if (!activeFormLayerId) return null;
    const state = useEditorStore.getState();
    if (!state.story) return null;
    for (const page of state.story.pages) {
      const l = page.layers.find((l) => l.id === activeFormLayerId);
      if (l) {
        const d = l.data;
        const fieldCount = d.fields?.length ?? 0;
        return d.formTitle ? `"${d.formTitle}" (${fieldCount} fields)` : `Form (${fieldCount} fields)`;
      }
    }
    return null;
  })();

  return (
    <div className="p-2 space-y-4">
      <div>
        <h3 className="panel-label mb-0.5">Form Builder</h3>
        <p className="text-xs" style={{ color: 'var(--t-muted)' }}>
          Build custom forms. Configure fields & submission endpoint in Properties.
        </p>
      </div>

      {/* Active form status */}
      {activeFormLabel ? (
        <div className="rounded-lg px-3 py-2 flex items-center gap-2"
             style={{ background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.35)' }}>
          <span style={{ fontSize: 14 }}>✏️</span>
          <div>
            <p className="text-xs font-semibold" style={{ color: 'var(--brand-300)' }}>Editing {activeFormLabel}</p>
            <p style={{ fontSize: 10, color: 'var(--t-muted)' }}>Field types below will be added to this form</p>
          </div>
        </div>
      ) : (
        <div className="rounded-lg px-3 py-2" style={{ background: 'var(--t-panel)', border: '1px solid var(--t-border)' }}>
          <p style={{ fontSize: 10, color: 'var(--t-muted)', lineHeight: 1.4 }}>
            No form on this page yet. Start from scratch, pick a template, or click a field type to create one.
          </p>
        </div>
      )}

      {/* Blank form */}
      <button
        onClick={addBlankForm}
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 border-dashed transition-all hover:opacity-90"
        style={{ borderColor: 'var(--brand-primary)', color: 'var(--brand-primary)', background: 'rgba(99,102,241,0.06)', fontWeight: 600, fontSize: 13 }}
      >
        <Plus size={15} />
        New Blank Form
      </button>

      {/* Templates */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--t-muted)' }}>
          Quick Templates
        </p>
        <div className="space-y-1.5">
          {FORM_TEMPLATES.map((tpl) => (
            <button
              key={tpl.id}
              onClick={() => addFormTemplate(tpl)}
              className="w-full text-left rounded-lg border transition-all group hover:border-indigo-500/60 hover:scale-[1.01]"
              style={{ background: 'var(--t-panel)', borderColor: 'var(--t-border)', padding: '10px 12px' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--t-panel2)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--t-panel)'; }}
            >
              <div className="flex items-center gap-2.5">
                <span style={{ fontSize: 20, lineHeight: 1 }}>{tpl.icon}</span>
                <div>
                  <p className="text-xs font-semibold" style={{ color: 'var(--t-text2)' }}>{tpl.label}</p>
                  <p className="text-xs" style={{ color: 'var(--t-muted)' }}>{tpl.description}</p>
                </div>
                <span className="ml-auto text-xs" style={{ color: 'var(--t-muted)' }}>
                  {tpl.fields.length} fields
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Field types — appends to existing form or creates one */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--t-muted)' }}>
          Add Field
        </p>
        <p style={{ fontSize: 10, color: 'var(--t-muted)', marginBottom: 8, lineHeight: 1.4 }}>
          {activeFormLabel
            ? 'Adds to the active form above ↑'
            : 'Creates a new form with this field'}
        </p>
        <div className="grid grid-cols-2 gap-1.5">
          {FIELD_TYPES.map((ft) => (
            <button
              key={ft.type}
              onClick={() => addFieldToForm(ft.type)}
              className="flex items-start gap-2 p-2 rounded-lg border text-left transition-all hover:scale-[1.02]"
              style={{ background: ft.color + '10', borderColor: ft.color + '30' }}
              onMouseEnter={e => { e.currentTarget.style.background = ft.color + '20'; e.currentTarget.style.borderColor = ft.color + '60'; }}
              onMouseLeave={e => { e.currentTarget.style.background = ft.color + '10'; e.currentTarget.style.borderColor = ft.color + '30'; }}
              title={ft.desc}
            >
              <span style={{ color: ft.color, marginTop: 1, flexShrink: 0 }}>{ft.icon}</span>
              <div>
                <p className="text-xs font-semibold" style={{ color: ft.color }}>{ft.label}</p>
                <p style={{ fontSize: 9, color: 'var(--t-muted)', lineHeight: 1.3 }}>{ft.desc}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
